/**
 * tpj-klaviyo-subscribe.js
 *
 * Newsletter forms (mega-menu, homepage mid-scroll, footer, and the shared
 * Shopify newsletter form) submit through Turnstile before the serverless
 * relay. Turnstile stays interaction-only and is executed on submit.
 *
 * Why a relay: Klaviyo's client-side subscribe API returns 202 but does NOT
 * actually subscribe for this account, so the subscribe must happen server-side
 * (relay holds the Klaviyo private key). The Shopify customer is created at
 * checkout; the native form's CAPTCHA blocks AJAX customer creation anyway.
 *
 * Forms are identified by their hidden input[name="contact[tags]"]=newsletter.
 * Klaviyo's own onsite popup does not use this marker and is left untouched.
 */
(function () {
  'use strict';

  var TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var TURNSTILE_ACTION = 'newsletter_signup';
  var RETRY_MESSAGE = "We couldn't sign you up right now. Please try again.";
  var TURNSTILE_LOAD_TIMEOUT_MS = window.TPJ_TURNSTILE_LOAD_TIMEOUT_MS || 8000;
  var TURNSTILE_EXEC_TIMEOUT_MS = window.TPJ_TURNSTILE_EXEC_TIMEOUT_MS || 15000;
  var RELAY_TIMEOUT_MS = window.TPJ_KLAVIYO_RELAY_TIMEOUT_MS || 10000;
  var turnstileLoader = null;

  // Per-form success message + the class the theme already styles for success.
  var FORMS = [
    { match: 'mega-menu-newsletter__form', cls: 'mega-menu-newsletter__success', fine: 'mega-menu-newsletter__fineprint', source: 'Mega-menu newsletter form', msg: "You're in! Check your inbox for your 20% off code." },
    { match: 'tpj-email-capture__form',    cls: 'note',                          fine: null,                              source: 'Homepage email capture form', msg: "You're in. Check your inbox for your 20% off code." },
    { match: 'footer-newsletter-form',     cls: 'tpj-footer-success',            fine: 'tpj-footer-fine-print',           source: 'Footer newsletter form', msg: "You're in! Check your inbox for your 20% off code." }
  ];

  function isNewsletterForm(form) {
    var tag = form.querySelector('input[name="contact[tags]"]');
    return tag && /(^|,)\s*newsletter\s*(,|$)/i.test(tag.value || '');
  }

  function ensureHoneypot(form, index) {
    var existing = form.querySelector('input[name="company_website"]');
    if (existing) return existing;

    var wrap = document.createElement('div');
    wrap.className = 'visually-hidden';
    wrap.setAttribute('data-tpj-honeypot', '');
    wrap.setAttribute('aria-hidden', 'true');

    var id = 'tpj-company-website-' + index;
    var label = document.createElement('label');
    label.setAttribute('for', id);
    label.textContent = 'Leave this field blank';

    var input = document.createElement('input');
    input.setAttribute('type', 'text');
    input.setAttribute('id', id);
    input.setAttribute('name', 'company_website');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('tabindex', '-1');
    input.setAttribute('aria-label', 'Leave this field blank');

    label.appendChild(input);
    wrap.appendChild(label);
    form.appendChild(wrap);
    return input;
  }

  function ensureStatus(form, m) {
    var status = form.querySelector('[data-tpj-newsletter-status]');
    if (status) return status;
    status = document.createElement('p');
    status.className = 'tpj-newsletter-error';
    status.setAttribute('data-tpj-newsletter-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    form.appendChild(status);
    return status;
  }

  function showError(form, m) {
    ensureStatus(form, m).textContent = RETRY_MESSAGE;
  }

  function clearStatus(form) {
    var status = form.querySelector('[data-tpj-newsletter-status]');
    if (status) status.textContent = '';
  }

  function meta(form) {
    for (var i = 0; i < FORMS.length; i++) {
      if (form.classList.contains(FORMS[i].match)) return FORMS[i];
    }
    return { cls: 'note', fine: null, source: 'Shopify newsletter form', msg: "You're in! Check your inbox for your 20% off code." };
  }

  function subscribe(relayUrl, email, source, challengeToken, honeypot) {
    var controller = window.AbortController ? new window.AbortController() : null;
    var timer;
    var deadline = new Promise(function (_, reject) {
      timer = window.setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('Newsletter relay timed out'));
      }, RELAY_TIMEOUT_MS);
    });
    var operation = fetch(relayUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller ? controller.signal : undefined,
      body: JSON.stringify({
        email: email,
        source: source,
        challenge_token: challengeToken,
        company_website: honeypot
      })
    }).then(function (response) {
      if (!response.ok) throw new Error('Newsletter relay rejected the request');
      return response.json();
    }).then(function (body) {
      if (!body || body.ok !== true) throw new Error('Newsletter relay returned an invalid response');
      return body;
    });
    return Promise.race([operation, deadline]).then(function (body) {
      window.clearTimeout(timer);
      return body;
    }, function (error) {
      window.clearTimeout(timer);
      throw error;
    });
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoader) return turnstileLoader;

    turnstileLoader = new Promise(function (resolve, reject) {
      var script = document.querySelector('script[data-tpj-turnstile-script]');
      var settled = false;
      var timer;
      function finish(callback, value) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        callback(value);
      }
      function fail(error) {
        if (script && script.parentNode) script.parentNode.removeChild(script);
        finish(reject, error);
      }
      if (!script) {
        script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-tpj-turnstile-script', '');
        document.head.appendChild(script);
      }
      script.onload = function () {
        if (window.turnstile) finish(resolve, window.turnstile);
        else fail(new Error('Turnstile did not initialize'));
      };
      script.onerror = function () { fail(new Error('Turnstile could not load')); };
      timer = window.setTimeout(function () {
        fail(new Error('Turnstile load timed out'));
      }, TURNSTILE_LOAD_TIMEOUT_MS);
    }).catch(function (error) {
      turnstileLoader = null;
      throw error;
    });

    return turnstileLoader;
  }

  function getChallengeToken(form, siteKey) {
    return loadTurnstile().then(function (turnstile) {
      return new Promise(function (resolve, reject) {
        var container = form.querySelector('[data-tpj-turnstile]');
        if (!container) {
          container = document.createElement('div');
          container.setAttribute('data-tpj-turnstile', '');
          form.appendChild(container);
        }

        var widgetId;
        var settled = false;
        var timer;
        function finish(callback, value) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          if (widgetId !== undefined) {
            try { turnstile.remove(widgetId); } catch (_) {}
          }
          callback(value);
        }

        widgetId = turnstile.render(container, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          execution: 'execute',
          action: TURNSTILE_ACTION,
          callback: function (token) { finish(resolve, token); },
          'error-callback': function () { finish(reject, new Error('Turnstile rejected the request')); },
          'expired-callback': function () { finish(reject, new Error('Turnstile token expired')); },
          'timeout-callback': function () { finish(reject, new Error('Turnstile timed out')); }
        });
        timer = window.setTimeout(function () {
          finish(reject, new Error('Turnstile challenge timed out'));
        }, TURNSTILE_EXEC_TIMEOUT_MS);
        turnstile.execute(widgetId);
      });
    });
  }

  function showSuccess(form, m) {
    // Hide adjacent fine-print (sibling outside the form), if any.
    if (m.fine) {
      var parent = form.parentElement;
      var fp = parent && parent.querySelector('.' + m.fine);
      if (fp) fp.style.display = 'none';
    }
    var p = document.createElement('p');
    p.className = m.cls;
    p.textContent = m.msg;
    form.innerHTML = '';
    form.appendChild(p);
    // The desktop mega-menu (sht-menu-header) auto-closes on `focusout` when
    // focus leaves the menu. Replacing the form's contents drops focus, which
    // would collapse the menu and hide this message. Park focus on the success
    // line (inside the menu) so the menu stays open until the user clicks away.
    p.setAttribute('tabindex', '-1');
    try { p.focus({ preventScroll: true }); } catch (_) { try { p.focus(); } catch (__) {} }
  }

  function onSubmit(e) {
    var form = e.currentTarget;
    var input = form.querySelector('input[name="contact[email]"]');
    if (!input) return;
    if (form.__tpjKlaviyoPending) {
      e.preventDefault();
      return;
    }

    if (!input.checkValidity()) {
      return;
    }
    e.preventDefault();

    var email = (input.value || '').trim();
    var m = meta(form);
    var honeypot = form.querySelector('input[name="company_website"]');
    if (honeypot && honeypot.value) {
      showError(form, m);
      return;
    }

    var siteKey = window.TPJ_TURNSTILE_SITE_KEY;
    var relayUrl = window.TPJ_KLAVIYO_RELAY_URL || 'https://tpj-klaviyo-relay.vercel.app/api/subscribe';
    if (!siteKey) {
      showError(form, m);
      return;
    }

    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    form.__tpjKlaviyoPending = true;
    if (btn) btn.disabled = true;
    // Disabling the button blurs it; in the mega-menu that focusout would close
    // the menu mid-request. Keep focus on the (still-present) email input so the
    // menu stays open while the relay call is in flight.
    try { input.focus({ preventScroll: true }); } catch (_) {}

    clearStatus(form);
    return getChallengeToken(form, siteKey)
      .then(function (token) {
        return subscribe(relayUrl, email, m.source, token, honeypot ? honeypot.value : '');
      })
      .then(function () { showSuccess(form, m); })
      .catch(function (err) {
        if (window.console) window.console.warn('[tpj-klaviyo] signup unavailable:', err);
        showError(form, m);
        form.__tpjKlaviyoPending = false;
        if (btn) btn.disabled = false;
      });
  }

  function init() {
    var forms = document.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) {
      if (isNewsletterForm(forms[i]) && !forms[i].__tpjKlaviyoBound) {
        forms[i].__tpjKlaviyoBound = true;
        ensureHoneypot(forms[i], i);
        forms[i].addEventListener('submit', onSubmit);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
